import React, { useState, useMemo, useRef } from "react";

import {
  SFTabsVertical,
  SFProgressBar,
  SFLoadingSpinner,
  SFStack,
  SFDialog,
  SFIcons,
  SFUseStyled,
  SFTypography,
  SFDropdown,
} from "@sf-core";
import { useTranslation } from "react-i18next";
import { getSkillpathCertificate } from "../../services/courseService";
import { getSkillPathDetails } from "../../services/skillPathService";
import { openLinkedinWindowPopup } from "../../helpers/openLinkedinWindowPopup";
import { useNavigate } from "react-router-dom";
import { getSkillPathUrl } from "../../constants/urls";
import { useDispatch } from "react-redux";
import { skillPathPageActions } from "../../redux/reducers/skillPathPageReducer";

const { styled } = SFUseStyled();

const StyledIcon = styled(SFIcons.SFInfoIcon)(() => ({
  visibility: "hidden",
}));

const StyledIconPopover = styled(SFIcons.SFMoreHorizIcon)(() => ({
  visibility: "hidden",
}));

const StyledTabContainer = styled("div")(() => ({
  "&.active": {
    "& .skill-path-description": {
      visibility: "visible !important",
    },
  },
}));

const SFTabsVerticalStyled = styled(SFTabsVertical)(({ theme }) => ({
  "&:hover": {
    "& .skill-path-description": {
      visibility: "visible",
    },
  },
}));

const SkillPathVerticalTabs = ({
  items,
  handleChange,
  value,
  loading = false,
  scrollable,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState({
    open: false,
    content: "",
    header: "",
  });
  const [currentItem, setCurrentItem] = useState("");
  const [dropdownState, setDropdownState] = useState({
    open: false,
    anchorEl: null,
  });
  const optionsRef = useRef();

  const clickOptionsHandler = (currentTarget, item) => {
    setCurrentItem(item);
    setDropdownState((curState) => ({
      ...curState,
      open: !curState.open,
      anchorEl: currentTarget,
    }));
  };

  const closeHandler = () => {
    setDropdownState((curState) => ({
      ...curState,
      open: false,
    }));
  };
  // Actions for the dropdown menu for each skillpath to share on linkedin and download certificate
  const actions = useMemo(() => {
    return [
      {
        label: t("academyScreen.skillpath.viewSkillsPath"),
        icon: <SFIcons.SFVisibilityIcon />,
        onClick: () => {
          dispatch(
            skillPathPageActions.setState({
              skillPathId: currentItem.id,
              domainId: currentItem.domainId,
              domainName: currentItem.domainName,
              domainTypeId: 0,
              domainStatus: currentItem.domainStatus,
            })
          );
          navigate(getSkillPathUrl(currentItem.id));
        },
      },
      ...(currentItem.completedDuration === currentItem.totalDuration
        ? [
          {
            label: t("academyScreen.skillpath.downloadCertificate"),
            icon: <SFIcons.SFDownloadIcon />,
            onClick: () => {
              //Check if the userBadgeId is already available in the item and download the certificate
              if (currentItem.userBadgeId) {
                getSkillpathCertificate(
                  currentItem.userBadgeId,
                  currentItem.name
                );
              } else {
                //Else call the API to get the userBadgeId and download the certificate
                getSkillPathDetails(currentItem.id).then((response) => {
                  getSkillpathCertificate(
                    response.data.userBadgeId,
                    currentItem.name
                  );
                });
              }
            },
          },
          {
            label: t("academyScreen.skillpath.shareOnLinkedin"),
            icon: <SFIcons.SFShareIcon />,
            onClick: () => {
              //Check if the userBadgeId is already available in the item and share on linkedin
              if (currentItem.userBadgeId)
                openLinkedinWindowPopup(currentItem.userBadgeId);
              else {
                //Else call the API to get the userBadgeId and share on linkedin
                getSkillPathDetails(currentItem.id).then((response) => {
                  openLinkedinWindowPopup(response.data.userBadgeId);
                });
              }
            },
          },
        ]
        : []),
    ];
  }, [currentItem]);

  return (
    <React.Fragment>
      <SFDialog
        size={"sm"}
        open={dialog.open}
        header={
          <SFTypography variant="academyCardTitle">
            {dialog.header}
          </SFTypography>
        }
        onClose={() => setDialog({ open: false })}
      >
        <SFTypography variant="body2">{dialog.content}</SFTypography>
      </SFDialog>

      <SFTabsVerticalStyled
        items={items.map((item) => ({
          id: item.id,
          label: (
            <StyledTabContainer
              className={`tab-container ${
                currentItem?.id === item.id && dropdownState.open
                  ? "active"
                  : ""
              }`}
            >
              <SFStack direction="row" alignItems="center" spacing={1}>
                <div>{item.name}</div>
                {item.description && (
                  <StyledIcon
                    className="skill-path-description"
                    color="white"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDialog({
                        open: true,
                        content: item.description,
                        header: item.name,
                      });
                    }}
                  />
                )}
                {value == item.id && loading ? (
                  <SFLoadingSpinner size={20} sx={{ ml: 2 }} />
                ) : null}
              </SFStack>

              <SFStack direction="row" alignItems="center" spacing={1}>
                <StyledIconPopover
                  className="skill-path-description"
                  onClick={(e) => {
                    e.stopPropagation();
                    clickOptionsHandler(e.currentTarget, item);
                  }}
                />

                <div style={{ width: "120px" }}>
                  <SFProgressBar
                    value={(item.completedDuration / item.totalDuration) * 100}
                    narrow
                  />
                </div>
              </SFStack>
            </StyledTabContainer>
          ),
        }))}
        variant="secondary"
        value={value}
        handleChange={handleChange}
        scrollable={scrollable}
      />
      <div ref={optionsRef}>
        <SFDropdown
          open={dropdownState.open}
          anchorEl={dropdownState.anchorEl}
          onClose={closeHandler}
          htmlPlacement={optionsRef.current}
          items={actions}
          positionArrow={85}
        />
      </div>
    </React.Fragment>
  );
};

export default SkillPathVerticalTabs;
